/**
 * ═══════════════════════════════════════════════════════════════════════════
 * PÁGINA DE FIRMA EXTERNA - BIOFIRMAS CTX
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * La ve el funcionario (médico, enfermero, etc.) cuando abre el link
 * del correo enviado por un sistema externo (HC u otros).
 * 
 * Flujo:
 *   PASO 1 → Bienvenida + Términos y condiciones
 *   PASO 2 → Captura de firma (canvas / texto / imagen)
 *   PASO 3 → Pantalla de éxito
 * 
 * Ruta en App.js:
 *   <Route path="/firma-externa/:token" element={<FirmaExternaPage />} />
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../utils/apiConfig';

function FirmaExternaPage() {
  const { token } = useParams();

  // ── Estado general ──
  const [paso, setPaso] = useState('cargando');
  // cargando | error | terminos | firma | exito
  const [errorMsg, setErrorMsg] = useState('');
  const [funcionario, setFuncionario] = useState(null);
  const [aceptoTerminos, setAceptoTerminos] = useState(false);
  const [guardando, setGuardando] = useState(false);

  // ── Estado de firma ──
  const [modoFirma, setModoFirma] = useState('canvas');
  const [nombreTexto, setNombreTexto] = useState('');
  const [imagenPreview, setImagenPreview] = useState(null);
  const fileInputRef = useRef(null);

  // ── Canvas ──
  const canvasRef = useRef(null);
  const canvasWrapperRef = useRef(null);
  const isDrawingRef = useRef(false);
  const hasInkRef = useRef(false);
  const [canvasKey, setCanvasKey] = useState(0);

  // ═══════════════════════════════════════════════════════════════════════
  // CARGAR DATOS DEL TOKEN
  // Usa el endpoint externo — no requiere API Key porque lo abre el navegador
  // ═══════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/external/firma/obtener/${token}`);
        const data = await res.json();

        if (!res.ok || !data.success) {
          const mensajes = {
            TOKEN_USADO:     'Este enlace ya fue utilizado. Tu firma ya fue registrada correctamente.',
            TOKEN_EXPIRADO:  'El enlace ha expirado. Solicita uno nuevo al área de sistemas.',
            TOKEN_INVALIDO:  'El enlace no es válido. Verifica que lo copiaste completo.'
          };
          setErrorMsg(mensajes[data.error] || 'Enlace inválido. Solicita uno nuevo al área de sistemas.');
          setPaso('error');
          return;
        }

        setFuncionario(data.funcionario);
        setPaso('terminos');
      } catch (err) {
        setErrorMsg('Error de conexión. Verifica tu internet e intenta de nuevo.');
        setPaso('error');
      }
    };

    cargarDatos();
  }, [token]);

  // ═══════════════════════════════════════════════════════════════════════
  // CANVAS
  // ═══════════════════════════════════════════════════════════════════════
  const getCanvasOffset = useCallback((clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }, []);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const wrapper = canvasWrapperRef.current;
    if (!canvas || !wrapper) return;

    const ratio = window.devicePixelRatio || 1;
    const displayWidth = Math.min(wrapper.getBoundingClientRect().width - 10, 400);
    const displayHeight = 160;

    canvas.style.width  = `${displayWidth}px`;
    canvas.style.height = `${displayHeight}px`;
    canvas.width  = Math.round(displayWidth * ratio);
    canvas.height = Math.round(displayHeight * ratio);

    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, displayWidth, displayHeight);
    ctx.strokeStyle = '#1a1e4b';
    ctx.lineWidth = 2.5;
    ctx.lineCap  = 'round';
    ctx.lineJoin = 'round';
    hasInkRef.current = false;
  }, []);

  const clearCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx   = canvas.getContext('2d');
    const ratio = window.devicePixelRatio || 1;
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width / ratio, canvas.height / ratio);
    hasInkRef.current = false;
  }, []);

  useEffect(() => {
    if (modoFirma !== 'canvas' || paso !== 'firma') return;

    const timer = setTimeout(() => {
      initCanvas();
      const canvas = canvasRef.current;
      if (!canvas) return;

      canvas.style.touchAction = 'none';
      const ctx = canvas.getContext('2d', { willReadFrequently: true });

      const onPointerDown = (e) => {
        e.preventDefault();
        canvas.setPointerCapture(e.pointerId);
        isDrawingRef.current = true;
        const { x, y } = getCanvasOffset(e.clientX, e.clientY);
        ctx.beginPath();
        ctx.moveTo(x, y);
      };
      const onPointerMove = (e) => {
        if (!isDrawingRef.current) return;
        e.preventDefault();
        const { x, y } = getCanvasOffset(e.clientX, e.clientY);
        ctx.lineTo(x, y);
        ctx.stroke();
        hasInkRef.current = true;
      };
      const endPointer = (e) => {
        if (isDrawingRef.current) {
          try { canvas.releasePointerCapture(e.pointerId); } catch {}
          isDrawingRef.current = false;
        }
      };

      canvas.addEventListener('pointerdown',   onPointerDown);
      canvas.addEventListener('pointermove',   onPointerMove);
      canvas.addEventListener('pointerup',     endPointer);
      canvas.addEventListener('pointercancel', endPointer);

      return () => {
        canvas.removeEventListener('pointerdown',   onPointerDown);
        canvas.removeEventListener('pointermove',   onPointerMove);
        canvas.removeEventListener('pointerup',     endPointer);
        canvas.removeEventListener('pointercancel', endPointer);
      };
    }, 100);

    return () => clearTimeout(timer);
  }, [modoFirma, canvasKey, paso, initCanvas, getCanvasOffset]);

  // ═══════════════════════════════════════════════════════════════════════
  // GUARDAR FIRMA
  // Llama al endpoint externo /api/external/firma/guardar/:token
  // ═══════════════════════════════════════════════════════════════════════
  const handleGuardar = async () => {
    let firmaBase64 = null;

    if (modoFirma === 'canvas') {
      if (!hasInkRef.current) {
        alert('Por favor dibuja tu firma antes de continuar.');
        return;
      }
      firmaBase64 = canvasRef.current.toDataURL('image/png');

    } else if (modoFirma === 'texto') {
      if (!nombreTexto.trim()) {
        alert('Por favor escribe tu nombre para continuar.');
        return;
      }
      const canvas = document.createElement('canvas');
      canvas.width  = 400;
      canvas.height = 120;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font      = '42px "Dancing Script", cursive';
      ctx.fillStyle = '#1a1e4b';
      ctx.textAlign = 'center';
      ctx.fillText(nombreTexto.trim(), canvas.width / 2, canvas.height / 2 + 15);
      firmaBase64 = canvas.toDataURL('image/png');

    } else if (modoFirma === 'imagen') {
      if (!imagenPreview) {
        alert('Por favor selecciona una imagen de tu firma.');
        return;
      }
      firmaBase64 = imagenPreview;
    }

    setGuardando(true);
    try {
      // ⭐ Endpoint externo — diferente al de firma interna
      const res = await fetch(`${API_BASE_URL}/api/external/firma/guardar/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ firmaBase64 })
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setPaso('exito');
      } else {
        alert(data.message || 'Error al guardar la firma. Intenta de nuevo.');
      }
    } catch (err) {
      alert('Error de conexión. Verifica tu internet e intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER — CARGANDO
  // ═══════════════════════════════════════════════════════════════════════
  if (paso === 'cargando') {
    return (
      <div style={styles.centrado}>
        <div style={styles.spinner}></div>
        <p style={{ color: '#555', marginTop: 16 }}>Verificando enlace...</p>
        <style>{spinnerCSS}</style>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER — ERROR
  // ═══════════════════════════════════════════════════════════════════════
  if (paso === 'error') {
    return (
      <div style={styles.centrado}>
        <style>{spinnerCSS}</style>
        <div style={{ ...styles.card, borderTop: '5px solid #dc3545', maxWidth: 440 }}>
          <div style={styles.iconCircle('#dc3545')}>⚠️</div>
          <h2 style={{ color: '#dc3545', margin: '12px 0 8px', textAlign: 'center' }}>Enlace no válido</h2>
          <p style={{ color: '#666', textAlign: 'center', lineHeight: 1.6 }}>{errorMsg}</p>
          <p style={{ color: '#999', fontSize: 13, textAlign: 'center', marginTop: 12 }}>
            ¿Necesitas ayuda? Escríbenos a{' '}
            <a href="mailto:soporte@colombianadetrasplantes.com" style={{ color: '#1a1e4b' }}>
              soporte@colombianadetrasplantes.com
            </a>
          </p>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER — PASO 1: TÉRMINOS
  // ═══════════════════════════════════════════════════════════════════════
  if (paso === 'terminos') {
    return (
      <>
        <style>{spinnerCSS}</style>
        <div style={styles.pagina}>
          <div style={styles.header}>
            <img src="/logo3.png" alt="BIOFIRMAS" style={{ height: 36, marginRight: 12 }} onError={(e) => e.target.style.display = 'none'} />
            <div>
              <div style={{ fontWeight: 'bold', fontSize: 16 }}>BIOFIRMAS CTX</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>Colombiana de Trasplantes S.A.S.</div>
            </div>
          </div>

          <div style={styles.contenido}>
            <div style={styles.card}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={styles.iconCircle('#1a1e4b')}>✍️</div>
                <h2 style={{ color: '#1a1e4b', margin: '12px 0 4px', fontSize: 22 }}>
                  Bienvenido/a, {funcionario?.nombres?.split(' ')[0]}
                </h2>
                <p style={{ color: '#666', fontSize: 14, margin: 0 }}>
                  Registro de Firma Digital — BIOFIRMAS CTX
                </p>
              </div>

              <div style={styles.infoBanner}>
                <p style={{ margin: 0, fontSize: 14, color: '#0c4a6e', lineHeight: 1.6 }}>
                  A continuación realizarás de forma <strong>segura y cifrada</strong> el registro
                  de tu firma digital, la cual será utilizada en los documentos institucionales
                  según corresponda.
                </p>
              </div>

              <div style={styles.terminosBox}>
                <h4 style={{ color: '#1a1e4b', marginTop: 0, marginBottom: 12, fontSize: 15 }}>
                  📋 Términos y Condiciones de Registro de Firma Digital
                </h4>
                <div style={{ fontSize: 13, color: '#444', lineHeight: 1.7, maxHeight: 220, overflowY: 'auto', paddingRight: 8 }}>
                  <p><strong>1. Propósito:</strong> Tu firma digital será registrada en el sistema BIOFIRMAS CTX de Colombiana de Trasplantes S.A.S. con el fin de validar documentos institucionales en los que participes.</p>
                  <p><strong>2. Seguridad:</strong> Tu firma será almacenada de forma cifrada en servidores seguros. Solo el personal autorizado podrá utilizarla en los documentos correspondientes.</p>
                  <p><strong>3. Protección de datos:</strong> El tratamiento de tu información se rige por la Ley 1581 de 2012 y el Decreto 1377 de 2013. Tu firma no será compartida con terceros no autorizados.</p>
                  <p><strong>4. Uso institucional:</strong> Autorizas a Colombiana de Trasplantes S.A.S. a utilizar tu firma exclusivamente en documentos institucionales relacionados con tu cargo y funciones.</p>
                  <p><strong>5. Vigencia:</strong> Este registro tiene vigencia indefinida hasta que solicites su actualización o eliminación al área de sistemas.</p>
                  <p><strong>6. Autenticidad:</strong> Al registrar tu firma confirmas que eres el titular y que corresponde a tu firma manuscrita habitual o una representación de ella.</p>
                </div>
              </div>

              <div
                style={{ ...styles.checkboxRow, background: aceptoTerminos ? '#f0fdf4' : '#f9fafb', borderColor: aceptoTerminos ? '#22c55e' : '#e5e7eb' }}
                onClick={() => setAceptoTerminos(!aceptoTerminos)}
              >
                <div style={{ ...styles.checkboxCustom, background: aceptoTerminos ? '#22c55e' : 'white', borderColor: aceptoTerminos ? '#22c55e' : '#d1d5db' }}>
                  {aceptoTerminos && <span style={{ color: 'white', fontSize: 14, fontWeight: 'bold' }}>✓</span>}
                </div>
                <p style={{ margin: 0, fontSize: 13, color: '#374151', lineHeight: 1.5 }}>
                  He leído y acepto los términos y condiciones de registro de firma digital,
                  así como la política de tratamiento de datos personales de Colombiana de Trasplantes S.A.S.
                </p>
              </div>

              <button
                onClick={() => setPaso('firma')}
                disabled={!aceptoTerminos}
                style={{ ...styles.btnPrimario, opacity: aceptoTerminos ? 1 : 0.4, cursor: aceptoTerminos ? 'pointer' : 'not-allowed' }}
              >
                Continuar al Registro de Firma →
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER — PASO 2: FIRMA
  // ═══════════════════════════════════════════════════════════════════════
  if (paso === 'firma') {
    return (
      <>
        <style>{`
          ${spinnerCSS}
          @import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@600&display=swap');
        `}</style>
        <div style={styles.pagina}>
          <div style={styles.header}>
            <img src="/logo3.png" alt="BIOFIRMAS" style={{ height: 36, marginRight: 12 }} onError={(e) => e.target.style.display = 'none'} />
            <div>
              <div style={{ fontWeight: 'bold', fontSize: 16 }}>BIOFIRMAS CTX</div>
              <div style={{ fontSize: 12, opacity: 0.8 }}>Registro de Firma Digital</div>
            </div>
          </div>

          <div style={styles.contenido}>
            <div style={styles.card}>
              <h2 style={{ color: '#1a1e4b', textAlign: 'center', marginBottom: 4, fontSize: 20 }}>
                ✍️ Registra tu Firma
              </h2>
              <p style={{ textAlign: 'center', color: '#666', fontSize: 13, marginBottom: 20 }}>
                Elige el método que prefieras
              </p>

              {/* Selector de modo */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
                {[
                  { key: 'canvas', icon: '✍️', label: 'Mano alzada' },
                  { key: 'texto',  icon: '⌨️', label: 'Con texto'   },
                  { key: 'imagen', icon: '📁', label: 'Subir imagen' }
                ].map(({ key, icon, label }) => (
                  <button
                    key={key}
                    onClick={() => { setModoFirma(key); if (key === 'canvas') setCanvasKey(p => p + 1); }}
                    style={{
                      flex: 1, padding: '10px 6px', borderRadius: 10, border: '2px solid',
                      borderColor: modoFirma === key ? '#1a1e4b' : '#e5e7eb',
                      background:  modoFirma === key ? '#f0f4ff' : 'white',
                      cursor: 'pointer', fontSize: 12, fontWeight: 600,
                      color: modoFirma === key ? '#1a1e4b' : '#6b7280',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4
                    }}
                  >
                    <span style={{ fontSize: 20 }}>{icon}</span>
                    {label}
                  </button>
                ))}
              </div>

              {/* CANVAS */}
              {modoFirma === 'canvas' && (
                <div>
                  <p style={{ color: '#666', fontSize: 13, textAlign: 'center', marginBottom: 8 }}>
                    👆 Dibuja tu firma en el recuadro
                  </p>
                  <div ref={canvasWrapperRef} style={{ background: '#fafafa', border: '2px dashed #d1d5db', borderRadius: 10, padding: 6, display: 'flex', justifyContent: 'center' }}>
                    <canvas key={canvasKey} ref={canvasRef} style={{ borderRadius: 8, cursor: 'crosshair', touchAction: 'none' }} />
                  </div>
                  <button onClick={clearCanvas} style={styles.btnSecundario}>🗑️ Limpiar</button>
                </div>
              )}

              {/* TEXTO */}
              {modoFirma === 'texto' && (
                <div>
                  <p style={{ color: '#666', fontSize: 13, textAlign: 'center', marginBottom: 8 }}>
                    ✏️ Escribe tu nombre completo
                  </p>
                  <input
                    type="text"
                    placeholder="Tu nombre completo"
                    value={nombreTexto}
                    onChange={(e) => setNombreTexto(e.target.value)}
                    style={{ width: '100%', padding: '10px 14px', fontSize: 16, border: '2px solid #e5e7eb', borderRadius: 8, marginBottom: 12, boxSizing: 'border-box' }}
                  />
                  <div style={{ background: '#fafafa', border: '2px dashed #d1d5db', borderRadius: 10, height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontFamily: '"Dancing Script", cursive', fontSize: 36, color: '#1a1e4b' }}>
                      {nombreTexto || 'Tu firma aquí'}
                    </span>
                  </div>
                </div>
              )}

              {/* IMAGEN */}
              {modoFirma === 'imagen' && (
                <div>
                  <p style={{ color: '#666', fontSize: 13, textAlign: 'center', marginBottom: 8 }}>
                    📁 Sube una imagen de tu firma (JPG, PNG — máx. 2MB)
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (!file) return;
                      if (file.size > 2 * 1024 * 1024) { alert('Máximo 2MB'); return; }
                      const reader = new FileReader();
                      reader.onloadend = () => setImagenPreview(reader.result);
                      reader.readAsDataURL(file);
                    }}
                  />
                  <button onClick={() => fileInputRef.current?.click()} style={styles.btnSecundario}>
                    📁 Seleccionar imagen
                  </button>
                  {imagenPreview && (
                    <div style={{ background: '#fafafa', border: '2px dashed #d1d5db', borderRadius: 10, padding: 10, textAlign: 'center', marginTop: 8 }}>
                      <img src={imagenPreview} alt="Firma" style={{ maxWidth: '100%', maxHeight: 140 }} />
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={handleGuardar}
                disabled={guardando}
                style={{ ...styles.btnPrimario, marginTop: 20 }}
              >
                {guardando
                  ? <><span style={styles.spinnerInline}></span> Guardando...</>
                  : '✅ Guardar Firma Digital'}
              </button>

              <button onClick={() => setPaso('terminos')} style={{ ...styles.btnSecundario, marginTop: 8 }}>
                ← Volver
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  // RENDER — PASO 3: ÉXITO
  // ═══════════════════════════════════════════════════════════════════════
  if (paso === 'exito') {
    return (
      <>
        <style>{`
          ${spinnerCSS}
          @keyframes bounceIn {
            0%   { opacity:0; transform:scale(0.3); }
            50%  { transform:scale(1.1); }
            70%  { transform:scale(0.9); }
            100% { opacity:1; transform:scale(1); }
          }
          @keyframes drawCheck {
            from { stroke-dashoffset:30; }
            to   { stroke-dashoffset:0; }
          }
        `}</style>
        <div style={styles.centrado}>
          <div style={{ ...styles.card, maxWidth: 400, textAlign: 'center' }}>
            <div style={{ ...styles.iconCircle('#10b981'), animation: 'bounceIn 0.6s ease', width: 70, height: 70, fontSize: 32 }}>
              <svg width="35" height="35" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" style={{ strokeDasharray: 30, strokeDashoffset: 0, animation: 'drawCheck 0.6s ease 0.2s both' }} />
              </svg>
            </div>
            <h2 style={{ color: '#1f2937', fontSize: 22, margin: '16px 0 8px' }}>
              ¡Firma Registrada!
            </h2>
            <p style={{ color: '#6b7280', fontSize: 14, lineHeight: 1.6 }}>
              Tu firma digital ha sido registrada exitosamente.
              Ya fue enviada al sistema correspondiente y estará disponible
              en los documentos institucionales según corresponda.
            </p>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 12, margin: '16px 0', fontSize: 13, color: '#166534' }}>
              🔒 Tu firma está protegida y cifrada. Solo el personal autorizado puede utilizarla.
            </div>
            <p style={{ color: '#9ca3af', fontSize: 12, marginTop: 8 }}>
              Puedes cerrar esta ventana.
            </p>
          </div>
        </div>
      </>
    );
  }

  return null;
}

// ═══════════════════════════════════════════════════════════════════════════
// ESTILOS
// ═══════════════════════════════════════════════════════════════════════════
const styles = {
  pagina: {
    minHeight: '100vh',
    background: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    background: 'linear-gradient(135deg, #1a1e4b, #2d3494)',
    color: 'white',
    padding: '14px 20px',
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.15)'
  },
  contenido: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    padding: '24px 16px'
  },
  card: {
    background: 'white',
    borderRadius: 16,
    padding: '28px 24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    width: '100%',
    maxWidth: 500
  },
  centrado: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    background: '#f8f9fa'
  },
  iconCircle: (color) => ({
    width: 60,
    height: 60,
    borderRadius: '50%',
    background: color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
    fontSize: 28,
    boxShadow: `0 8px 20px ${color}44`
  }),
  infoBanner: {
    background: '#e0f2fe',
    border: '1px solid #bae6fd',
    borderRadius: 10,
    padding: '14px 16px',
    marginBottom: 20
  },
  terminosBox: {
    border: '1px solid #e5e7eb',
    borderRadius: 10,
    padding: 16,
    marginBottom: 16,
    background: '#fafafa'
  },
  checkboxRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 14px',
    border: '2px solid',
    borderRadius: 10,
    cursor: 'pointer',
    marginBottom: 16,
    transition: 'all 0.2s'
  },
  checkboxCustom: {
    width: 22,
    height: 22,
    border: '2px solid',
    borderRadius: 5,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 1,
    transition: 'all 0.2s'
  },
  btnPrimario: {
    width: '100%',
    background: 'linear-gradient(135deg, #1a1e4b, #2d3494)',
    color: 'white',
    border: 'none',
    borderRadius: 50,
    padding: '13px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(26,30,75,0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8
  },
  btnSecundario: {
    width: '100%',
    background: '#f3f4f6',
    color: '#4b5563',
    border: 'none',
    borderRadius: 50,
    padding: '10px',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    marginTop: 8
  },
  spinner: {
    width: 40,
    height: 40,
    border: '4px solid #e5e7eb',
    borderTop: '4px solid #1a1e4b',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  spinnerInline: {
    display: 'inline-block',
    width: 16,
    height: 16,
    border: '2px solid rgba(255,255,255,0.4)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }
};

const spinnerCSS = `
  @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
`;

export default FirmaExternaPage;